import type { GridColDef } from '@mui/x-data-grid';
import type { InvoiceOverview } from '@weft/api-definition';
import type { InvoiceReviewStatus } from '@weft/types';
import { INVOICE_REVIEW_STATUSES } from '@weft/types';
import type { ValueOf } from '@weft/utils';
import { raiseUnreachable } from '@weft/utils';
import { VyTableCell } from 'apps/app/src/components/new-design/table/vy-table/VyTableCell';
import { formatDate } from 'apps/app/src/utils/date';
import { Price } from '../../../../components/design/Price';
import { InvoiceBillingStatusLabel } from '../../../invoices/InvoicesPage/invoices-page-components/InvoiceBillingStatusLabel';

export const INVOICES_COLUMNS_KEYS = {
  CONTRACT_NAME: 'contractName',
  STATUS: 'status',
  WORKFLOW: 'reviewStatus',
  ID: 'id',
  CUSTOMER_NAME: 'customerName',
  START_DATE: 'startDate',
  TOTAL_AMOUNT: 'totalAmount',
} as const;

export type InvoicesColumnsKey = ValueOf<typeof INVOICES_COLUMNS_KEYS>;

export const CUSTOMER_INVOICES_TABLE_COLUMNS: GridColDef<InvoiceOverview>[] = [
  {
    field: INVOICES_COLUMNS_KEYS.STATUS,
    valueGetter: ({ row }) => row.billingStatus,
    headerName: 'Status',
    headerClassName: 'pl-7',
    cellClassName: 'pl-6',
    width: 200,
    hideSortIcons: true,
    renderCell: ({ value }) => (
      <InvoiceBillingStatusLabel billingStatus={value}/>
    ),
  },
  {
    field: INVOICES_COLUMNS_KEYS.ID,
    headerName: 'Invoice',
    flex: 1,
    renderCell: ({ value }) => <span className="font-sans text-black">{value?.slice(-5)}</span>,
  },
  {
    field: INVOICES_COLUMNS_KEYS.TOTAL_AMOUNT,
    headerName: 'Amount',
    flex: 1,
    renderCell: (params) => {
      const amount = params.row.revenueBreakdown.total;
      const currency = params.row.currency;

      return (
        <span className="text-label font-sans">
          <Price
            className="font-sans text-black"
            fontSize={14}
            currency={currency}
          >
            {amount}
          </Price>
        </span>
      );
    },
  },
  {
    field: INVOICES_COLUMNS_KEYS.START_DATE,
    headerName: 'Date',
    flex: 1,
    renderCell: ({ row }) => formatDate(row.billingCycle.startTime),
    valueGetter: ({ row }) => row.billingCycle.startTime,
  },
  {
    field: INVOICES_COLUMNS_KEYS.WORKFLOW,
    headerName: 'Workflow',
    flex: 1,
    renderCell: ({ value }) => {
      return (
        <div className="flex items-center justify-center">
          <p className="font-sans text-black">{getReviewStatusLabel(value)}</p>
        </div>
      );
    },
  },
  {
    field: INVOICES_COLUMNS_KEYS.CONTRACT_NAME,
    headerName: 'Contract name',
    flex: 1,
    renderCell: ({ value }) => (
      <VyTableCell
        containerClassName='justify-start'
        value={value}
      />
    ),
  },
];

export function getReviewStatusLabel(reviewStatus: InvoiceReviewStatus) {
  switch (reviewStatus) {
    case INVOICE_REVIEW_STATUSES.Approved:
      return 'Approved';
    case INVOICE_REVIEW_STATUSES.InReview:
      return 'In review';
    case INVOICE_REVIEW_STATUSES.Draft:
      return 'Draft';
    case INVOICE_REVIEW_STATUSES.Synced:
      return 'Synced';
    default:
      return raiseUnreachable(reviewStatus);
  }
}